import React, { useState } from 'react';
import { neo4jService } from '../../services/neo4jService';
import { lookupBarcode, searchByTerm } from '../../services/barcodeLookupService';
import { menu_categories } from '../../misc/objs';
import { Camera, ScanLine, Search } from 'lucide-react';
import BarcodeScanner from '../BarcodeScanner/BarcodeScanner';
import { DEFAULT_PRODUCT_CONDITION, PRODUCT_CONDITIONS } from '../../config/productCondition';
import { DEFAULT_PRODUCT_POLICIES } from '../../config/productPolicies';
import './AddProduct.css';

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error(`Failed to read ${file.name}`));
    reader.readAsDataURL(file);
  });

const AddProduct = () => {
  const [lookupMode, setLookupMode] = useState('barcode'); // 'barcode' or 'search'
  const [barcode, setBarcode] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [barcodeLoading, setBarcodeLoading] = useState(false);
  const [barcodeError, setBarcodeError] = useState('');
  const [barcodeSuccess, setBarcodeSuccess] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isScannerOpen, setIsScannerOpen] = useState(false);

  const [product, setProduct] = useState({
    name: '',
    price: '',
    originalPrice: '',
    category: '',
    brand: '',
    condition: DEFAULT_PRODUCT_CONDITION,
    description: '',
    ...DEFAULT_PRODUCT_POLICIES,
    rating: 0,
    reviewsCount: 0,
    availability: true,
    inStock: true,
    qty_instock: '',
    model: '',
    sku: '',
    main_image: '',
    tag: '',
    isNew: true,
    isFeatured: false,
    features: [],
    images: [],
    specifications: {}
  });

  // Get all subcategories from menu_categories
  const allSubCategories = menu_categories.reduce((acc, category) => {
    return [...acc, ...category.subCategories];
  }, []).sort();

  const [additionalImages, setAdditionalImages] = useState(['']);

  const [specKey, setSpecKey] = useState('');
  const [specValue, setSpecValue] = useState('');

  const applyProductData = (result) => {
    const allImages = result.images || [];
    const mainImg = allImages.length > 0 ? allImages[0] : '';
    const extraImgs = allImages.length > 1 ? allImages.slice(1) : [];

    setProduct(prev => ({
      ...prev,
      name: result.name || prev.name,
      brand: result.brand || prev.brand,
      model: result.model || prev.model,
      description: result.description || prev.description,
      main_image: mainImg,
      sku: result.barcode || prev.sku,
    }));

    setAdditionalImages(extraImgs.length > 0 ? extraImgs : ['']);

    setSearchResults([]);
    setBarcodeSuccess(`Found: ${result.name || 'Product data loaded'}${allImages.length > 0 ? ` (${allImages.length} image${allImages.length > 1 ? 's' : ''})` : ''}`);
  };

  const handleBarcodeLookup = async (barcodeOverride) => {
    const lookupValue = barcodeOverride || barcode;
    setBarcodeError('');
    setBarcodeSuccess('');
    setSearchResults([]);
    setBarcodeLoading(true);

    try {
      const result = await lookupBarcode(lookupValue);
      applyProductData(result);
    } catch (error) {
      setBarcodeError(error.message);
    } finally {
      setBarcodeLoading(false);
    }
  };

  const handleBarcodeScan = (scannedBarcode) => {
    setBarcode(scannedBarcode);
    setIsScannerOpen(false);
    handleBarcodeLookup(scannedBarcode);
  };

  const handleSearchLookup = async () => {
    setBarcodeError('');
    setBarcodeSuccess('');
    setSearchResults([]);
    setBarcodeLoading(true);

    try {
      const results = await searchByTerm(searchQuery);
      if (results.length === 1) {
        applyProductData(results[0]);
      } else {
        setSearchResults(results);
      }
    } catch (error) {
      setBarcodeError(error.message);
    } finally {
      setBarcodeLoading(false);
    }
  };

  const handleLookupKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (lookupMode === 'barcode') {
        handleBarcodeLookup();
      } else {
        handleSearchLookup();
      }
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setProduct(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFeatureChange = (index, value) => {
    const newFeatures = [...product.features];
    newFeatures[index] = value;
    setProduct(prev => ({
      ...prev,
      features: newFeatures
    }));
  };

  const addFeature = () => {
    setProduct(prev => ({
      ...prev,
      features: [...prev.features, '']
    }));
  };

  const removeFeature = (index) => {
    const newFeatures = product.features.filter((_, i) => i !== index);
    setProduct(prev => ({
      ...prev,
      features: newFeatures
    }));
  };

  const handleImageChange = (index, value) => {
    const newImages = [...additionalImages];
    newImages[index] = value;
    setAdditionalImages(newImages);
  };

  const handleMainImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }

    try {
      const imageDataUrl = await readFileAsDataUrl(file);
      setProduct(prev => ({
        ...prev,
        main_image: imageDataUrl
      }));
    } catch (error) {
      setBarcodeError(error.message);
    } finally {
      e.target.value = '';
    }
  };

  const handleAdditionalImagesUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) {
      return;
    }

    try {
      const uploadedImages = await Promise.all(files.map(readFileAsDataUrl));
      setAdditionalImages(prev => {
        const existingImages = prev.filter(img => img.trim() !== '');
        const nextImages = [...existingImages, ...uploadedImages];
        return nextImages.length > 0 ? nextImages : [''];
      });
    } catch (error) {
      setBarcodeError(error.message);
    } finally {
      e.target.value = '';
    }
  };

  const addImage = () => {
    setAdditionalImages([...additionalImages, '']);
  };

  const removeImage = (index) => {
    const newImages = additionalImages.filter((_, i) => i !== index);
    setAdditionalImages(newImages);
  };

  const addSpecification = () => {
    if (specKey && specValue) {
      setProduct(prev => ({
        ...prev,
        specifications: {
          ...prev.specifications,
          [specKey]: specValue
        }
      }));
      setSpecKey('');
      setSpecValue('');
    }
  };

  const removeSpecification = (key) => {
    const newSpecs = { ...product.specifications };
    delete newSpecs[key];
    setProduct(prev => ({
      ...prev,
      specifications: newSpecs
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const productToAdd = {
        ...product,
        images: additionalImages.filter(img => img.trim() !== ''),
        price: parseFloat(product.price),
        originalPrice: parseFloat(product.originalPrice),
        qty_instock: parseInt(product.qty_instock)
      };

      await neo4jService.createProduct(productToAdd);
      // Reset form
      setProduct({
        name: '',
        price: '',
        originalPrice: '',
        category: '',
        brand: '',
        condition: DEFAULT_PRODUCT_CONDITION,
        description: '',
        ...DEFAULT_PRODUCT_POLICIES,
        rating: 0,
        reviewsCount: 0,
        availability: true,
        inStock: true,
        qty_instock: '',
        model: '',
        sku: '',
        main_image: '',
        tag: '',
        isNew: true,
        isFeatured: false,
        features: [],
        images: [],
        specifications: {}
      });
      setAdditionalImages(['']);
    } catch (error) {
      console.error('Error adding product:', error);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="add-product-form">
      {isScannerOpen && (
        <BarcodeScanner
          onScan={handleBarcodeScan}
          onClose={() => setIsScannerOpen(false)}
        />
      )}

      <div className="form-section barcode-section">
        <h3>{lookupMode === 'barcode' ? <ScanLine size={20} /> : <Search size={20} />} Product Lookup</h3>
        <div className="lookup-mode-toggle">
          <button
            type="button"
            className={`mode-btn ${lookupMode === 'barcode' ? 'active' : ''}`}
            onClick={() => { setLookupMode('barcode'); setSearchResults([]); setBarcodeError(''); setBarcodeSuccess(''); }}
          >
            <ScanLine size={16} /> Barcode
          </button>
          <button
            type="button"
            className={`mode-btn ${lookupMode === 'search' ? 'active' : ''}`}
            onClick={() => { setLookupMode('search'); setSearchResults([]); setBarcodeError(''); setBarcodeSuccess(''); }}
          >
            <Search size={16} /> Search
          </button>
        </div>
        <p className="barcode-hint">
          {lookupMode === 'barcode'
            ? 'Enter or scan a barcode to auto-fill product details'
            : 'Search by product name, brand, or keyword'}
        </p>
        <div className="barcode-input-row">
          {lookupMode === 'barcode' ? (
            <div className="barcode-input-group">
              <input
                type="text"
                value={barcode}
                onChange={(e) => setBarcode(e.target.value)}
                onKeyDown={handleLookupKeyDown}
                placeholder="Enter UPC, EAN, or ISBN barcode"
                className="barcode-input"
                disabled={barcodeLoading}
              />
              <button
                type="button"
                className="barcode-scan-button"
                onClick={() => {
                  setLookupMode('barcode');
                  setBarcodeError('');
                  setBarcodeSuccess('');
                  setIsScannerOpen(true);
                }}
                disabled={barcodeLoading}
              >
                <Camera size={18} />
                Scan
              </button>
            </div>
          ) : (
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleLookupKeyDown}
              placeholder="e.g. Nike Air Max, Samsung Galaxy..."
              className="barcode-input search-input"
              disabled={barcodeLoading}
            />
          )}
          <button
            type="button"
            onClick={lookupMode === 'barcode' ? handleBarcodeLookup : handleSearchLookup}
            className="barcode-lookup-button"
            disabled={barcodeLoading || (lookupMode === 'barcode' ? !barcode.trim() : !searchQuery.trim())}
          >
            {barcodeLoading ? 'Looking up...' : 'Lookup'}
          </button>
        </div>
        {barcodeError && <div className="barcode-error">{barcodeError}</div>}
        {barcodeSuccess && <div className="barcode-success">{barcodeSuccess}</div>}

        {searchResults.length > 0 && (
          <div className="search-results">
            <p className="search-results-count">{searchResults.length} product{searchResults.length > 1 ? 's' : ''} found — select one:</p>
            <div className="search-results-list">
              {searchResults.map((result, index) => (
                <div
                  key={index}
                  className="search-result-item"
                  onClick={() => applyProductData(result)}
                >
                  <div className="search-result-info">
                    <span className="search-result-name">{result.name}</span>
                    {result.brand && <span className="search-result-brand">{result.brand}</span>}
                    <span className="search-result-meta">
                      {(result.images?.length || 0) > 0 ? `${result.images.length} image${result.images.length > 1 ? 's' : ''}` : 'No images'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="form-section">
        <h3>Basic Information</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>Product Name</label>
            <input
              type="text"
              name="name"
              value={product.name}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Price ($)</label>
            <input
              type="number"
              name="price"
              value={product.price}
              onChange={handleChange}
              step="0.01"
              required
            />
          </div>

          <div className="form-group">
            <label>Original Price ($)</label>
            <input
              type="number"
              name="originalPrice"
              value={product.originalPrice}
              onChange={handleChange}
              step="0.01"
            />
          </div>

          <div className="form-group">
            <label>Category</label>
            <select
              name="category"
              value={product.category}
              onChange={handleChange}
              required
            >
              <option value="">Select a category</option>
              {allSubCategories.map((subCategory, index) => (
                <option key={index} value={subCategory}>
                  {subCategory}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Brand</label>
            <input
              type="text"
              name="brand"
              value={product.brand}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Condition</label>
            <select
              name="condition"
              value={product.condition}
              onChange={handleChange}
              required
            >
              {PRODUCT_CONDITIONS.map(condition => (
                <option key={condition} value={condition}>
                  {condition}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Model</label>
            <input
              type="text"
              name="model"
              value={product.model}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label>SKU</label>
            <input
              type="text"
              name="sku"
              value={product.sku}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Tag</label>
            <input
              type="text"
              name="tag"
              value={product.tag}
              onChange={handleChange}
            />
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>Inventory</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>Availability</label>
            <select
              name="availability"
              value={product.availability}
              onChange={handleChange}
            >
              <option value={true}>In Stock</option>
              <option value={false}>Out of Stock</option>
              <option value={false}>Pre-order</option>
            </select>
          </div>

          <div className="form-group">
            <label>Quantity in Stock</label>
            <input
              type="number"
              name="qty_instock"
              value={product.qty_instock}
              onChange={handleChange}
              min="0"
            />
          </div>

          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                name="isNew"
                checked={product.isNew}
                onChange={handleChange}
              />
              Mark as New Product
            </label>
          </div>

          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                name="isFeatured"
                checked={product.isFeatured}
                onChange={handleChange}
              />
              Feature this product
            </label>
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>Description</h3>
        <div className="form-group">
          <textarea
            name="description"
            value={product.description}
            onChange={handleChange}
            rows="4"
            required
          ></textarea>
        </div>
      </div>

      <div className="form-section">
        <h3>Product Policies</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>Shipping Title</label>
            <input
              type="text"
              name="shippingTitle"
              value={product.shippingTitle}
              onChange={handleChange}
              placeholder="Shipping"
            />
          </div>

          <div className="form-group">
            <label>Shipping Text</label>
            <input
              type="text"
              name="shippingDescription"
              value={product.shippingDescription}
              onChange={handleChange}
              placeholder="Ships in 3-5 business days"
            />
          </div>

          <div className="form-group">
            <label>Shipping Cost</label>
            <input
              type="text"
              name="shippingCost"
              value={product.shippingCost}
              onChange={handleChange}
              placeholder="Free or $10.00"
            />
          </div>

          <div className="form-group">
            <label>Delivery Title</label>
            <input
              type="text"
              name="deliveryTitle"
              value={product.deliveryTitle}
              onChange={handleChange}
              placeholder="Delivery"
            />
          </div>

          <div className="form-group">
            <label>Delivery Text</label>
            <input
              type="text"
              name="deliveryDescription"
              value={product.deliveryDescription}
              onChange={handleChange}
              placeholder="Local delivery available"
            />
          </div>

          <div className="form-group">
            <label>Delivery Cost</label>
            <input
              type="text"
              name="deliveryCost"
              value={product.deliveryCost}
              onChange={handleChange}
              placeholder="Free or $25.00"
            />
          </div>

          <div className="form-group">
            <label>Warranty Title</label>
            <input
              type="text"
              name="warrantyTitle"
              value={product.warrantyTitle}
              onChange={handleChange}
              placeholder="2 Year Warranty"
            />
          </div>

          <div className="form-group">
            <label>Warranty Text</label>
            <input
              type="text"
              name="warrantyDescription"
              value={product.warrantyDescription}
              onChange={handleChange}
              placeholder="Full coverage"
            />
          </div>

          <div className="form-group">
            <label>Return Policy Title</label>
            <input
              type="text"
              name="returnTitle"
              value={product.returnTitle}
              onChange={handleChange}
              placeholder="30 Day Return"
            />
          </div>

          <div className="form-group">
            <label>Return Policy Text</label>
            <input
              type="text"
              name="returnDescription"
              value={product.returnDescription}
              onChange={handleChange}
              placeholder="Money back guarantee"
            />
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>Images</h3>
        <div className="form-group">
          <label>Main Image URL</label>
          <div className="image-input-row">
            <input
              type="url"
              name="main_image"
              value={product.main_image}
              onChange={handleChange}
              required
            />
            {product.main_image && (
              <img src={product.main_image} alt="Main preview" className="image-preview" />
            )}
          </div>
          <label className="upload-button">
            Upload Main Image
            <input
              type="file"
              accept="image/*"
              onChange={handleMainImageUpload}
              className="file-input"
            />
          </label>
        </div>

        <div className="form-group">
          <label>Additional Images</label>
          {additionalImages.map((url, index) => (
            <div key={index} className="image-entry">
              <div className="array-input">
                <input
                  type="url"
                  value={url}
                  onChange={(e) => handleImageChange(index, e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => removeImage(index)}
                  className="remove-button"
                >
                  Remove
                </button>
              </div>
              {url && (
                <img src={url} alt={`Preview ${index + 1}`} className="image-preview" />
              )}
            </div>
          ))}
          <label className="upload-button">
            Upload Additional Images
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleAdditionalImagesUpload}
              className="file-input"
            />
          </label>
          <button type="button" onClick={addImage} className="add-button">
            Add Image
          </button>
        </div>
      </div>

      <div className="form-section">
        <h3>Features</h3>
        {product.features.map((feature, index) => (
          <div key={index} className="array-input">
            <input
              type="text"
              value={feature}
              onChange={(e) => handleFeatureChange(index, e.target.value)}
              placeholder="Enter feature"
            />
            <button
              type="button"
              onClick={() => removeFeature(index)}
              className="remove-button"
            >
              Remove
            </button>
          </div>
        ))}
        <button type="button" onClick={addFeature} className="add-button">
          Add Feature
        </button>
      </div>

      <div className="form-section">
        <h3>Specifications</h3>
        <div className="specs-input">
          <input
            type="text"
            value={specKey}
            onChange={(e) => setSpecKey(e.target.value)}
            placeholder="Specification name"
          />
          <input
            type="text"
            value={specValue}
            onChange={(e) => setSpecValue(e.target.value)}
            placeholder="Specification value"
          />
          <button type="button" onClick={addSpecification} className="add-button">
            Add Spec
          </button>
        </div>
        <div className="specs-list">
          {Object.entries(product.specifications).map(([key, value]) => (
            <div key={key} className="spec-item">
              <span>{key}: {value}</span>
              <button
                type="button"
                onClick={() => removeSpecification(key)}
                className="remove-button"
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="form-actions">
        <button type="submit" className="submit-button">
          Add Product
        </button>
      </div>
    </form>
  );
};

export default AddProduct;
